import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useCustomer from "common/datahooks/useCustomer";
import { del, patch, post } from "common/helpers/HTTP";
import { typedObjectEntries } from "common/helpers/utils";

import type { CreateAgencyConfigSchemaType } from "../schema";
import type { AgencySettings } from "../types";

type AgencySettingsUpdates = Omit<AgencySettings, "logo" | "favicon"> & {
  logo: File;
  favicon: File;
};

async function removeFaviconOrLogoRequest(
  customizationId: string,
  type: "logo" | "favicon",
): Promise<AgencySettings> {
  return (await del(`agency/customizations/${customizationId}/${type}`))
    .customization;
}

async function updateAgencySettingsRequest(
  customizationId: string,
  updates: Partial<AgencySettingsUpdates>,
): Promise<AgencySettings> {
  const formData = new FormData();

  typedObjectEntries(updates).forEach(([key, value]) => {
    formData.append(
      key,
      key === "logo" || key === "favicon" ? value : String(value),
    );
  });

  return (await patch(`agency/customizations/${customizationId}`, formData))
    .customization;
}

async function createAgencySettingsRequest(
  customizationId: string,
  settings: CreateAgencyConfigSchemaType,
) {
  return (await post(`agency/customizations/${customizationId}`, settings))
    .customization;
}

export default function useAgencySettingsMutations() {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const queryClient = useQueryClient();
  const { agencySettingsKey } = getQueryKeys(workspaceId);
  const { customerInfo } = useCustomer();

  const {
    mutateAsync: updateAgencySettings,
    isPending: isUpdatingAgencySettings,
  } = useMutation({
    mutationFn: ({ updates }: { updates: Partial<AgencySettingsUpdates> }) =>
      updateAgencySettingsRequest(customerInfo.chargebee_id, updates),
    onSuccess: (settings) => {
      queryClient.setQueryData<AgencySettings>(agencySettingsKey, settings);
    },
    onError: () => toast.error("Error updating settings"),
  });

  const {
    mutateAsync: removeAgencyLogoOrFavicon,
    isPending: isRemovingAgencyLogoOrFavicon,
  } = useMutation({
    mutationFn: ({ type }: { type: "favicon" | "logo" }) =>
      removeFaviconOrLogoRequest(customerInfo.chargebee_id, type),
    onSuccess: (settings) => {
      queryClient.setQueryData<AgencySettings>(agencySettingsKey, settings);
    },
    onError: () => toast.error("Error removing logo"),
  });

  const {
    mutateAsync: createAgencySettings,
    isPending: isCreatingAgencySettings,
  } = useMutation({
    mutationFn: ({ settings }: { settings: CreateAgencyConfigSchemaType }) =>
      createAgencySettingsRequest(customerInfo.chargebee_id, settings),
    onSuccess: (settings) => {
      queryClient.setQueryData<AgencySettings>(agencySettingsKey, settings);
    },
    onError: () => toast.error("Error updating settings"),
  });

  return {
    createAgencySettings,
    isCreatingAgencySettings,
    updateAgencySettings,
    isUpdatingAgencySettings,
    removeAgencyLogoOrFavicon,
    isRemovingAgencyLogoOrFavicon,
  };
}
